/**
 * Image Upload Component
 * Handles drag & drop and click-to-upload functionality for custom product images.
 * Uploads images to Cloudinary and stores the URL in line item properties.
 */

// Cloudinary configuration
const CLOUDINARY_CONFIG = {
  cloudName: 'dafzzzjxx',
  uploadPreset: 'cursor',
  folder: 'customer_tattoos'
};

class ImageUploadComponent extends HTMLElement {
  /** @type {HTMLInputElement} */
  fileInput;

  /** @type {HTMLInputElement} */
  imageDataInput;

  /** @type {HTMLInputElement} */
  imageStatusInput;

  /** @type {HTMLElement} */
  dropzone;

  /** @type {HTMLElement} */
  uploadPrompt;

  /** @type {HTMLElement} */
  previewArea;

  /** @type {HTMLImageElement} */
  previewImage;

  /** @type {HTMLButtonElement} */
  removeBtn;

  /** @type {HTMLElement} */
  loadingIndicator;

  /** @type {HTMLElement} */
  statusMessage;

  /** @type {HTMLElement} */
  errorMessage;

  /** @type {HTMLElement} */
  successMessage;

  /** @type {number} */
  maxSize;

  /** @type {string[]} */
  acceptedFormats;

  /** @type {boolean} */
  isRequired;

  /** @type {AbortController} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Get refs
    this.fileInput = this.querySelector('[ref="fileInput"]');
    this.imageDataInput = this.querySelector('[ref="imageDataInput"]');
    this.imageStatusInput = this.querySelector('[ref="imageStatusInput"]');
    this.dropzone = this.querySelector('[ref="dropzone"]');
    this.uploadPrompt = this.querySelector('[ref="uploadPrompt"]');
    this.previewArea = this.querySelector('[ref="previewArea"]');
    this.previewImage = this.querySelector('[ref="previewImage"]');
    this.removeBtn = this.querySelector('[ref="removeBtn"]');
    this.loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    this.statusMessage = this.querySelector('[ref="statusMessage"]');
    this.errorMessage = this.querySelector('[ref="errorMessage"]');
    this.successMessage = this.querySelector('[ref="successMessage"]');

    // Get config
    this.maxSize = parseInt(this.dataset.maxSize || '5242880', 10);
    this.acceptedFormats = (this.dataset.acceptedFormats || 'image/jpeg,image/png,image/webp').split(',').map(f => f.trim());
    this.isRequired = this.dataset.required === 'true';

    // Ensure initial state is correct
    this.#ensureInitialState();

    // Bind events
    this.fileInput?.addEventListener('change', this.#handleFileSelect.bind(this), { signal });
    this.dropzone?.addEventListener('click', this.#handleDropzoneClick.bind(this), { signal });
    this.dropzone?.addEventListener('keydown', this.#handleKeydown.bind(this), { signal });
    this.dropzone?.addEventListener('dragover', this.#handleDragOver.bind(this), { signal });
    this.dropzone?.addEventListener('dragleave', this.#handleDragLeave.bind(this), { signal });
    this.dropzone?.addEventListener('drop', this.#handleDrop.bind(this), { signal });
    this.removeBtn?.addEventListener('click', this.#handleRemove.bind(this), { signal });

    // Custom validation for required field
    if (this.isRequired && this.imageDataInput) {
      this.imageDataInput.addEventListener('invalid', this.#handleInvalid.bind(this), { signal });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Ensures initial state is correct
   */
  #ensureInitialState() {
    // Make sure loading is hidden
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
    // Make sure preview is hidden
    if (this.previewArea) {
      this.previewArea.style.display = 'none';
    }
    // Make sure prompt is visible
    if (this.uploadPrompt) {
      this.uploadPrompt.style.display = '';
    }
    // Make sure success/error messages are hidden
    if (this.successMessage) {
      this.successMessage.style.display = 'none';
    }
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
    }
  }

  /**
   * Handles click on dropzone to trigger file input
   * @param {MouseEvent} event
   */
  #handleDropzoneClick(event) {
    // Don't trigger if clicking the remove button
    if (event.target?.closest('[ref="removeBtn"]')) return;
    this.fileInput?.click();
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.fileInput?.click();
    }
  }

  /**
   * Handles drag over event
   * @param {DragEvent} event
   */
  #handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzone?.classList.add('is-dragging');
  }

  /**
   * Handles drag leave event
   * @param {DragEvent} event
   */
  #handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzone?.classList.remove('is-dragging');
  }

  /**
   * Handles drop event
   * @param {DragEvent} event
   */
  #handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzone?.classList.remove('is-dragging');

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.#processFile(files[0]);
    }
  }

  /**
   * Handles file selection from input
   * @param {Event} event
   */
  #handleFileSelect(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const files = input.files;
    if (files?.length) {
      this.#processFile(files[0]);
    }
  }

  /**
   * Processes the selected file
   * @param {File} file
   */
  async #processFile(file) {
    // Clear previous messages
    this.#hideError();
    this.#hideSuccess();
    this.dropzone?.classList.remove('has-error');

    // Validate file type
    if (!this.acceptedFormats.includes(file.type)) {
      this.#showError(this.dataset.errorFormat || 'Invalid file format. Please use JPG, PNG, or WEBP.');
      return;
    }

    // Validate file size
    if (file.size > this.maxSize) {
      this.#showError(this.dataset.errorSize || 'File is too large. Please upload a smaller image.');
      return;
    }

    // Show loading state
    this.#showLoading();

    try {
      // Upload to Cloudinary
      const imageUrl = await this.#uploadToCloudinary(file);
      
      // Update the hidden input with the Cloudinary URL
      if (this.imageDataInput) {
        this.imageDataInput.value = imageUrl;
        // Remove required validation error state
        this.imageDataInput.setCustomValidity('');
      }
      
      // Update the visible status input with friendly message (shown at checkout)
      if (this.imageStatusInput) {
        this.imageStatusInput.value = this.dataset.uploadedLabel || '✓ Özel tasarım yüklendi';
      }

      // Hide loading first
      this.#hideLoading();

      // Show preview
      this.#showPreview(imageUrl);
      
      // Show success message
      this.#showSuccess(this.dataset.successText || 'Görsel başarıyla yüklendi!');
      
      // Update status for screen readers
      this.#updateStatus('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      this.#hideLoading();
      this.#showError(this.dataset.errorUpload || 'Failed to upload image. Please try again.');
    }
  }

  /**
   * Uploads image to Cloudinary
   * @param {File} file
   * @returns {Promise<string>} The Cloudinary URL
   */
  async #uploadToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
    formData.append('folder', CLOUDINARY_CONFIG.folder);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Cloudinary error:', errorData);
      throw new Error('Upload failed');
    }

    const data = await response.json();
    
    // Return the secure URL (https)
    return data.secure_url;
  }

  /**
   * Shows the image preview
   * @param {string} imageUrl
   */
  #showPreview(imageUrl) {
    if (this.previewImage) {
      this.previewImage.src = imageUrl;
    }
    if (this.uploadPrompt) {
      this.uploadPrompt.style.display = 'none';
    }
    if (this.previewArea) {
      this.previewArea.style.display = '';
      this.previewArea.removeAttribute('hidden');
    }
  }

  /**
   * Hides the image preview
   */
  #hidePreview() {
    if (this.previewImage) {
      this.previewImage.src = '';
    }
    if (this.previewArea) {
      this.previewArea.style.display = 'none';
      this.previewArea.setAttribute('hidden', '');
    }
    if (this.uploadPrompt) {
      this.uploadPrompt.style.display = '';
      this.uploadPrompt.removeAttribute('hidden');
    }
  }

  /**
   * Shows loading indicator
   */
  #showLoading() {
    if (this.uploadPrompt) {
      this.uploadPrompt.style.display = 'none';
    }
    if (this.previewArea) {
      this.previewArea.style.display = 'none';
    }
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = '';
      this.loadingIndicator.removeAttribute('hidden');
    }
    this.#updateStatus(this.dataset.uploadingText || 'Uploading image...');
  }

  /**
   * Hides loading indicator
   */
  #hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
      this.loadingIndicator.setAttribute('hidden', '');
    }
  }

  /**
   * Shows success message
   * @param {string} message
   */
  #showSuccess(message) {
    if (this.successMessage) {
      this.successMessage.textContent = message;
      this.successMessage.style.display = '';
      this.successMessage.removeAttribute('hidden');
    }
    this.dropzone?.classList.add('has-success');
  }

  /**
   * Hides success message
   */
  #hideSuccess() {
    if (this.successMessage) {
      this.successMessage.style.display = 'none';
      this.successMessage.setAttribute('hidden', '');
      this.successMessage.textContent = '';
    }
    this.dropzone?.classList.remove('has-success');
  }

  /**
   * Shows error message
   * @param {string} message
   */
  #showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = '';
      this.errorMessage.removeAttribute('hidden');
    }
    this.dropzone?.classList.add('has-error');
    this.#updateStatus(message);
  }

  /**
   * Hides error message
   */
  #hideError() {
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
      this.errorMessage.setAttribute('hidden', '');
      this.errorMessage.textContent = '';
    }
    this.dropzone?.classList.remove('has-error');
  }

  /**
   * Updates status message for screen readers
   * @param {string} message
   */
  #updateStatus(message) {
    if (this.statusMessage) {
      this.statusMessage.textContent = message;
    }
  }

  /**
   * Handles remove button click
   * @param {MouseEvent} event
   */
  #handleRemove(event) {
    event.preventDefault();
    event.stopPropagation();
    
    // Clear the file input
    if (this.fileInput) {
      this.fileInput.value = '';
    }
    
    // Clear the hidden inputs
    if (this.imageDataInput) {
      this.imageDataInput.value = '';
    }
    if (this.imageStatusInput) {
      this.imageStatusInput.value = '';
    }
    
    // Hide preview and show prompt
    this.#hidePreview();
    this.#hideError();
    this.#hideSuccess();
    
    // Update status
    this.#updateStatus('Image removed');
  }

  /**
   * Handles invalid event for required validation
   * @param {Event} event
   */
  #handleInvalid(event) {
    event.preventDefault();
    this.#showError(this.dataset.errorRequired || 'Please upload an image for your custom product.');
    this.dropzone?.classList.add('has-error');
    this.dropzone?.focus();
  }
}

if (!customElements.get('image-upload-component')) {
  customElements.define('image-upload-component', ImageUploadComponent);
}
